/** Stock log page (Phase 4.3) — общий журнал изменений остатков. */

import { useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowUp } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api } from "@/lib/api";

interface StockLogEntry {
  id: number;
  product_id: number;
  variant_id: number | null;
  quantity_before: number;
  quantity_after: number;
  delta: number;
  reason: string | null;
  order_id: number | null;
  changed_by: number | null;
  created_at: string;
}

interface StockLogResponse {
  data: StockLogEntry[];
  pagination: { total: number };
}

export function StockLogPage(): JSX.Element {
  const { t } = useTranslation();
  const urlParams = new URLSearchParams(window.location.search);
  const [productIdFilter, setProductIdFilter] = useState<string>(
    urlParams.get("product_id") ?? "",
  );

  const { data, isLoading } = useQuery({
    queryKey: ["admin-stock-log", productIdFilter],
    queryFn: async () => {
      const params: Record<string, unknown> = { per_page: 200 };
      if (productIdFilter.trim()) params.product_id = Number(productIdFilter);
      const { data } = await api.get<StockLogResponse>("/stock-log", {
        params,
      });
      return data;
    },
  });

  const items = data?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-heading">{t("nav.stock")}</h1>
        <div className="flex items-end gap-2">
          <div className="space-y-1">
            <Label className="text-xs">{t("stockLog.productIdFilter")}</Label>
            <Input
              type="number"
              value={productIdFilter}
              onChange={(e) => setProductIdFilter(e.target.value)}
              placeholder={t("stockLog.allProducts")}
              className="w-48"
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-96" />
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          {t("stockLog.empty")}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("stockLog.when")}</TableHead>
                  <TableHead>{t("stockLog.product")}</TableHead>
                  <TableHead className="text-right">{t("stockLog.before")}</TableHead>
                  <TableHead className="text-right">{t("stockLog.delta")}</TableHead>
                  <TableHead className="text-right">{t("stockLog.after")}</TableHead>
                  <TableHead>{t("stockLog.reason")}</TableHead>
                  <TableHead>{t("stockLog.context")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(row.created_at).toLocaleString("ru-RU")}
                    </TableCell>
                    <TableCell>
                      <a
                        href={`/products/${row.product_id}/edit`}
                        className="text-primary hover:underline"
                      >
                        #{row.product_id}
                      </a>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {row.quantity_before}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      <span
                        className={
                          row.delta > 0
                            ? "text-green-600 dark:text-green-400"
                            : "text-red-600 dark:text-red-400"
                        }
                      >
                        {row.delta > 0 ? (
                          <ArrowUp className="inline w-3 h-3" />
                        ) : (
                          <ArrowDown className="inline w-3 h-3" />
                        )}
                        {Math.abs(row.delta)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-medium">
                      {row.quantity_after}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs font-mono">
                        {row.reason ?? "—"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {row.order_id && (
                        <a
                          href={`/orders/${row.order_id}`}
                          className="text-primary hover:underline"
                        >
                          {t("stockLog.order")}: #{row.order_id}
                        </a>
                      )}
                      {row.changed_by && (
                        <span>
                          {row.order_id ? " · " : ""}
                          {t("stockLog.admin")}: #{row.changed_by}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
